import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useParams,
} from 'react-router-dom';
import { DashboardPage } from 'pages/Dashboard';
import { FC, PropsWithChildren, useContext } from 'react';
import { UserContext } from 'src/context/UserContext';
import { CustomerDashboard } from 'components/customers/CustomerDashboard';
import { CustomerDetailsPage } from 'components/customers/CustomerDetails';
import { DriverDashboard } from 'pages/Driver/DriverDashboard';
import { DriverDetailsPage } from 'pages/Driver/DriverDetailsPage';
import { ContainerDashboard } from 'pages/Container/ContainerDashboard';
import { ContainerDetailsPage } from 'pages/Container/ContainerDetailsPage';
import { SignInPage } from 'pages/SignIn';
import { OrderDashboardPage } from 'components/orders/OrderDashboard';
import { InvoiceDashboardPage } from 'pages/Invoice/InvoiceDashboardPage';
import { InvoiceDetailsPage } from 'pages/Invoice/InvoiceDetailsPage';
import { TruckDashboard } from 'pages/Truck/TruckDashboard';
import { TruckDetailsPage } from 'pages/Truck/TruckDetailsPage';
import { OrderDetailsPage } from 'components/orders/Details/OrderDetails';
import SettingDashboard from 'src/pages/Settings/SettingDashboard';
import { OrganizationDashboard } from 'pages/Organization/OrganizationDashboard';
import { FeatureFlagsDashboard } from 'pages/Feature/FeatureFlagsDashboard';
import { PricingDashboard } from 'pages/Pricing/PricingDashboard';
import { PricingTestResults } from 'pages/Pricing/PricingTestResults';
import { CleanupDashboard } from 'pages/Cleanup';
import { OrganizationProvider } from 'src/context/OrganizationContext';
import { LeadsDashboard } from 'components/leads/LeadsDashboard';
import { LocationPage } from 'pages/Location/LocationPage';
import { MaterialDashboardPage } from 'pages/Materials/MaterialDashboard';
import { GenericErrorFallback } from './components/GenericErrorFallback';
import { DiscountsPage } from 'pages/Discounts/DiscountCodePage';
import { CityFeesDashboard } from 'pages/Pricing/CityFeesDashboard';

const AuthenticatedRoute: FC<PropsWithChildren> = ({ children }) => {
  const { user, userRoles } = useContext(UserContext);
  const location = useLocation();
  if (!(user && userRoles?.includes('admin'))) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <OrganizationProvider>{children}</OrganizationProvider>;
};

const ContainerRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/dumpsters/${id}`} />;
};

export const Router = () => {
  const { user, userRoles } = useContext(UserContext);
  const router = createBrowserRouter([
    {
      path: '/login',
      element: user && userRoles?.includes('admin') ? <Navigate replace to="/" /> : <SignInPage />,
      errorElement: <GenericErrorFallback />,
    },
    {
      path: '/',
      element: (
        <AuthenticatedRoute>
          <DashboardPage />
        </AuthenticatedRoute>
      ),
      errorElement: <GenericErrorFallback />,
      children: [
        {
          path: '',
          element: <Navigate to="/orders" />,
        },
        {
          path: 'customers',
          element: <CustomerDashboard />,
        },
        {
          path: 'customers/:id',
          element: <CustomerDetailsPage />,
        },
        {
          path: 'leads',
          element: <LeadsDashboard />,
        },
        {
          path: 'orders',
          element: <OrderDashboardPage />,
        },
        {
          path: 'orders/:id',
          element: <OrderDetailsPage />,
        },
        {
          path: 'pricing/discounts',
          element: <DiscountsPage />,
        },
        {
          path: 'invoices',
          element: <InvoiceDashboardPage />,
        },
        {
          path: 'invoices/:id',
          element: <InvoiceDetailsPage />,
        },
        {
          path: 'trucks',
          element: <TruckDashboard />,
        },
        {
          path: 'trucks/:id',
          element: <TruckDetailsPage />,
        },
        {
          path: 'drivers',
          element: <DriverDashboard />,
        },
        {
          path: 'drivers/:id',
          element: <DriverDetailsPage />,
        },
        {
          path: 'containers',
          element: <Navigate to="/dumpsters" />,
        },
        {
          path: 'containers/:id',
          element: <ContainerRedirect />,
        },
        {
          path: 'dumpsters',
          element: <ContainerDashboard />,
        },
        {
          path: 'dumpsters/:id',
          element: <ContainerDetailsPage />,
        },
        {
          path: 'settings/materials',
          element: <MaterialDashboardPage />,
        },
        {
          path: 'settings',
          element: <Navigate to="/settings/users" />,
        },
        {
          path: 'settings/add-ons',
          element: <SettingDashboard />,
        },
        {
          path: 'settings/products',
          element: <SettingDashboard />,
        },
        {
          path: 'settings/users',
          element: <SettingDashboard />,
        },
        {
          path: 'settings/container-types',
          element: <SettingDashboard />,
        },
        {
          path: 'settings/cleanups',
          element: <CleanupDashboard />,
        },
        {
          path: 'settings/locations',
          element: <LocationPage />,
        },
        {
          path: 'organizations',
          element: <OrganizationDashboard />,
        },
        {
          path: 'pricing/rules',
          element: <PricingDashboard />,
        },
        {
          path: 'pricing/test-results',
          element: <PricingTestResults />,
        },
        {
          path: 'pricing/city-fees',
          element: <CityFeesDashboard />,
        },
        {
          path: 'organizations/:id/locations',
          element: <LocationPage />,
        },
        {
          path: 'features',
          element: <FeatureFlagsDashboard />,
        },
      ],
    },
  ]);
  return (
    <div className="bg-background w-full h-full min-h-screen flex flex-col">
      <RouterProvider router={router} />
    </div>
  );
};
